import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  ArrowUpRight,
  Bell,
  Check,
  CheckCheck,
  Clock3,
  Fingerprint,
  Mail,
  MailOpen,
  RefreshCw,
  User,
} from "lucide-react";
import { patch, type Row } from "../api";
import {
  activityLabel,
  displayReturnId,
  returnSystemText,
  taxText,
} from "../terminology";
import { Badge, Button, Modal } from "../ui";
import { DataTable } from "./DataTable";
import { workspaceOperationError } from "../presentation";
import "./operational-views.css";

const asText = (value: unknown): string =>
  typeof value === "string"
    ? value
    : value === null || value === undefined
      ? ""
      : String(value);
export const notificationErrorMessage = (
  error: unknown,
  action: "read" | "unread" | "refresh",
): string =>
  workspaceOperationError(
    error,
    action === "refresh"
      ? "load the latest notifications"
      : `mark the selected notification as ${action}`,
  );
export function notificationState(
  record: Row,
): "Read" | "Unread" | "Not supplied" {
  return record.read === true
    ? "Read"
    : record.read === false
      ? "Unread"
      : "Not supplied";
}
export function operationalTimestamp(value: unknown): {
  raw: string;
  display: string;
  valid: boolean;
} {
  const raw = asText(value);
  if (!raw) return { raw, display: "Not supplied", valid: false };
  const date = new Date(raw);
  return Number.isNaN(date.getTime())
    ? { raw, display: "Invalid source timestamp", valid: false }
    : {
        raw,
        display: date.toLocaleString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          timeZoneName: "short",
        }),
        valid: true,
      };
}
export function normalizeAuditEvent(record: Row): Row {
  return {
    source: record,
    id: asText(record.id),
    actor: asText(record.actor),
    action: asText(record.action),
    objectType: asText(record.type),
    objectId: asText(record.subjectId),
    summary: asText(record.summary),
    createdAt: asText(record.createdAt),
  };
}
function Timestamp({ value }: { value: unknown }) {
  const time = operationalTimestamp(value);
  return time.valid ? (
    <time dateTime={time.raw} title={time.raw}>
      {time.display}
    </time>
  ) : (
    <span title={time.raw || undefined}>{time.display}</span>
  );
}

export interface NotificationCenterProps {
  records: Row[];
  refresh: () => Promise<void>;
  notify: (message: string) => void;
  loading?: boolean;
  returnRecords?: readonly Row[];
  returnDisplayIds?: ReadonlyMap<string, string>;
  technical?: boolean;
}
export function NotificationCenter({
  records,
  refresh,
  notify,
  loading = false,
  returnRecords = [],
  returnDisplayIds,
  technical = false,
}: NotificationCenterProps) {
  const [tab, setTab] = useState<"all" | "unread" | "read">("all");
  const [selectedId, setSelectedId] = useState<string | null>(null),
    [updates, setUpdates] = useState<Map<string, Row>>(new Map());
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const inFlight = useRef(false);
  const systemText = (value: unknown) =>
    returnDisplayIds
      ? returnSystemText(value, returnRecords, returnDisplayIds)
      : taxText(value);
  // Every new parent response supersedes local copies of successful PATCH responses.
  useEffect(() => {
    setUpdates(new Map());
  }, [records]);
  const rows = useMemo<Row[]>(
    () =>
      records.map((record) => {
        const source = updates.get(String(record.id)) ?? record;
        return { ...source, readState: notificationState(source) };
      }),
    [records, updates],
  );
  const unread = rows.filter((record) => record.read === false);
  const filtered = rows.filter(
    (record) =>
      tab === "all" ||
      (tab === "unread" ? record.read === false : record.read === true),
  );
  const selected = rows.find((record) => String(record.id) === selectedId);
  const close = useCallback(() => setSelectedId(null), []);

  async function changeRead(targets: Row[], read: boolean) {
    if (inFlight.current) return;
    const changedTargets = [
      ...new Map(
        targets
          .filter((record) => record.id && record.read !== read)
          .map((record) => [String(record.id), record]),
      ).values(),
    ];
    if (!changedTargets.length) {
      notify(
        `The selected notifications are already ${read ? "read" : "unread"}.`,
      );
      return;
    }
    inFlight.current = true;
    setBusy(true);
    setError("");
    let changed = 0;
    const failures: string[] = [];
    try {
      for (const record of changedTargets) {
        try {
          const result = await patch<Row>(
            `/notifications/${encodeURIComponent(String(record.id))}`,
            { read },
          );
          if (
            String(result.id) !== String(record.id) ||
            typeof result.read !== "boolean"
          )
            throw new Error(
              "The service response could not confirm the notification state. Refresh before retrying.",
            );
          setUpdates((current) =>
            new Map(current).set(String(record.id), result),
          );
          changed++;
        } catch (failure) {
          failures.push(
            notificationErrorMessage(failure, read ? "read" : "unread"),
          );
        }
      }
      try {
        await refresh();
      } catch (failure) {
        failures.push(notificationErrorMessage(failure, "refresh"));
      }
      if (changed)
        notify(
          `${changed} notification${changed === 1 ? "" : "s"} marked ${read ? "read" : "unread"}.${failures.length ? " Some requests need attention." : ""}`,
        );
      if (failures.length)
        setError(
          `${changed} of ${changedTargets.length} changes confirmed. ${[...new Set(failures)].join(" ")}`,
        );
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }
  async function reload() {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    try {
      await refresh();
      notify("Notifications refreshed from the workspace service.");
    } catch (failure) {
      setError(notificationErrorMessage(failure, "refresh"));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  return (
    <section className="zt-operational" aria-label="Notification center">
      <div className="zt-operation-heading">
        <div>
          <span className="zt-operation-symbol">
            <Bell size={21} />
          </span>
          <div>
            <h2>A little less noise. A clear next step.</h2>
            <p>Notifications recorded for your account in this workspace.</p>
          </div>
        </div>
        <div className="zt-operation-actions">
          <Button disabled={busy || loading} onClick={() => void reload()}>
            <RefreshCw size={14} />
            Refresh
          </Button>
          <Button
            disabled={busy || loading || !unread.length}
            onClick={() => void changeRead(unread, true)}
          >
            <CheckCheck size={15} />
            Mark loaded unread as read
          </Button>
        </div>
      </div>
      <div
        className="zt-notification-tabs"
        role="group"
        aria-label="Filter notifications by read state"
      >
        {(["all", "unread", "read"] as const).map((value) => (
          <button
            type="button"
            key={value}
            className={tab === value ? "is-active" : ""}
            aria-pressed={tab === value}
            onClick={() => setTab(value)}
          >
            {value === "all"
              ? "All notifications"
              : value === "unread"
                ? "Unread"
                : "Read"}
            <span>
              {value === "all"
                ? rows.length
                : rows.filter((record) =>
                    value === "read"
                      ? record.read === true
                      : record.read === false,
                  ).length}
            </span>
          </button>
        ))}
        <span className="zt-notification-scope">Loaded records only</span>
      </div>
      {error && (
        <div className="zt-operation-error" role="alert">
          {taxText(error)}
        </div>
      )}
      <DataTable
        records={filtered}
        getSearchText={(record) =>
          `${systemText(record.title)} ${systemText(record.body)}`
        }
        getRowId={(record) => String(record.id)}
        loading={loading}
        title="Workspace notifications"
        onInspect={(record) => setSelectedId(String(record.id))}
        emptyMessage={
          tab === "unread"
            ? "No unread notifications in the loaded records."
            : tab === "read"
              ? "No read notifications in the loaded records."
              : "No notifications have been returned by the workspace service."
        }
        columns={[
          {
            key: "title",
            label: "Notification",
            getTitle: (value) => systemText(value),
            render: (value, record) => (
              <span
                className={`zt-notification-title ${record.read === false ? "is-unread" : ""}`}
              >
                {record.read === false ? (
                  <Mail size={15} />
                ) : (
                  <MailOpen size={15} />
                )}
                <span>
                  {systemText(value) || "Untitled source notification"}
                </span>
              </span>
            ),
          },
          {
            key: "body",
            label: "Message",
            getTitle: (value) => systemText(value),
            render: (value) => systemText(value),
          },
          {
            key: "readState",
            label: "Status",
            render: (value) => <Badge>{asText(value)}</Badge>,
          },
          {
            key: "createdAt",
            label: "Recorded",
            render: (value) => <Timestamp value={value} />,
          },
          {
            key: "actions",
            label: "Manage",
            sortable: false,
            render: (_, record) => (
              <Button
                disabled={busy || !record.id}
                onClick={() => void changeRead([record], record.read !== true)}
              >
                {record.read === true ? (
                  <Mail size={14} />
                ) : (
                  <Check size={14} />
                )}
                {record.read === true ? "Mark unread" : "Mark read"}
              </Button>
            ),
          },
        ]}
        bulkActions={[
          {
            label: "Mark selected read",
            onAction: (selectedRows) => changeRead(selectedRows, true),
          },
          {
            label: "Mark selected unread",
            onAction: (selectedRows) => changeRead(selectedRows, false),
          },
        ]}
      />
      <p className="zt-operation-caption">
        <Fingerprint size={13} />
        Read status is a personal notification setting. It is not an approval,
        review decision, or certification.
      </p>
      {selected && (
        <Modal
          title={systemText(selected.title) || "Notification"}
          close={close}
        >
          <div className="zt-notification-detail">
            <div className="zt-notification-meta">
              <Badge>{notificationState(selected)}</Badge>
              <span>
                <Clock3 size={13} />
                <Timestamp value={selected.createdAt} />
              </span>
            </div>
            <p>
              {systemText(selected.body) || "No message body was supplied."}
            </p>
            {selected.readAt && (
              <dl>
                <div>
                  <dt>Read at</dt>
                  <dd>
                    <Timestamp value={selected.readAt} />
                  </dd>
                </div>
              </dl>
            )}
            {technical && (
              <details>
                <summary>Technical Details</summary>
                <dl>
                  <div>
                    <dt>Original notification</dt>
                    <dd>
                      <code>{asText(selected.id)}</code>
                    </dd>
                  </div>
                </dl>
              </details>
            )}
            <div className="form-actions">
              <Button onClick={close}>Close</Button>
              <Button
                disabled={busy}
                variant="primary"
                onClick={() =>
                  void changeRead([selected], selected.read !== true)
                }
              >
                {selected.read === true ? "Mark unread" : "Mark read"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </section>
  );
}

export interface AuditEventsProps {
  records: Row[];
  inspect?: (record: Row) => void;
  loading?: boolean;
  title?: string;
  returnDisplayIds?: ReadonlyMap<string, string>;
  returnRecords?: readonly Row[];
}
export function AuditEvents({
  records,
  inspect,
  loading = false,
  title = "Recorded workspace activity",
  returnDisplayIds,
  returnRecords = [],
}: AuditEventsProps) {
  const normalized = useMemo(() => records.map(normalizeAuditEvent), [records]);
  const actors = new Set(
    normalized.map((record) => record.actor).filter(Boolean),
  ).size;
  const actions = new Set(
    normalized.map((record) => record.action).filter(Boolean),
  ).size;
  return (
    <section className="zt-operational" aria-label={taxText(title)}>
      <div className="zt-operation-heading">
        <div>
          <span className="zt-operation-symbol">
            <Activity size={21} />
          </span>
          <div>
            <h2>{taxText(title)}</h2>
            <p>
              Trace the recorded actor, action, original object, and timestamp.
            </p>
          </div>
        </div>
        <Badge>Actual service records</Badge>
      </div>
      <div className="zt-audit-counts">
        <span>
          <strong>{normalized.length.toLocaleString()}</strong>loaded events
        </span>
        <span>
          <strong>{actors.toLocaleString()}</strong>recorded actors
        </span>
        <span>
          <strong>{actions.toLocaleString()}</strong>distinct actions
        </span>
        <span>
          <Clock3 size={13} />
          Times shown in your device’s timezone
        </span>
      </div>
      <DataTable
        records={normalized}
        loading={loading}
        title={title}
        getSearchText={(record) =>
          `${activityLabel(record.action)} ${taxText(record.objectType)} ${returnDisplayIds ? returnSystemText(record.summary, returnRecords, returnDisplayIds) : taxText(record.summary)} ${record.objectType === "case" ? displayReturnId({ id: record.objectId }, returnDisplayIds) : ""}`
        }
        getRowId={(record, index) => record.id || `event:${index}`}
        onInspect={inspect ? (record) => inspect(record.source) : undefined}
        emptyMessage="No workspace activity records were supplied. No event or review history is inferred."
        columns={[
          {
            key: "action",
            label: "Recorded action",
            getTitle: (value) => activityLabel(value),
            render: (value) => (
              <span className="zt-audit-action">
                <Activity size={14} />
                <span>{value ? activityLabel(value) : "Not supplied"}</span>
              </span>
            ),
          },
          {
            key: "actor",
            label: "Actor",
            render: (value) => (
              <span className="zt-audit-actor">
                <User size={13} />
                {asText(value) || "Not supplied"}
              </span>
            ),
          },
          {
            key: "createdAt",
            label: "Recorded at",
            render: (value) => <Timestamp value={value} />,
          },
          {
            key: "objectType",
            label: "Object type",
            getTitle: (value) => taxText(value),
            render: (value) => taxText(value) || "Not supplied",
          },
          {
            key: "objectId",
            label: "Object reference",
            getTitle: (value, record) =>
              record.objectType === "case"
                ? displayReturnId({ id: record.objectId }, returnDisplayIds)
                : asText(value),
            render: (value, record) => (
              <code>
                {record.objectType === "case"
                  ? displayReturnId({ id: record.objectId }, returnDisplayIds)
                  : asText(value) || "Not supplied"}
              </code>
            ),
          },
          {
            key: "summary",
            label: "Recorded summary",
            getTitle: (value) =>
              returnDisplayIds
                ? returnSystemText(value, returnRecords, returnDisplayIds)
                : taxText(value),
            render: (value) =>
              (returnDisplayIds
                ? returnSystemText(value, returnRecords, returnDisplayIds)
                : taxText(value)) || "Not supplied",
          },
          {
            key: "id",
            label: "Event identity",
            render: (value) => <code>{asText(value) || "Not supplied"}</code>,
          },
          ...(inspect
            ? [
                {
                  key: "inspect",
                  label: "Source",
                  sortable: false,
                  render: (_: unknown, record: Row) => (
                    <Button onClick={() => inspect(record.source)}>
                      <ArrowUpRight size={14} />
                      Inspect
                    </Button>
                  ),
                },
              ]
            : []),
        ]}
      />
      <p className="zt-operation-caption">
        <Fingerprint size={13} />
        This view includes only the activity returned by the workspace service.
        It does not claim a complete immutable compliance ledger or independent
        audit verification.
      </p>
    </section>
  );
}
